import classnames from "classnames";
import moment from "moment";
import { KeyboardEvent, MouseEvent, useState } from "react";
import { useSelector } from "react-redux";

import { dayMonthFormat, hourMinuteFormat } from "shared/lib/datetime";

import { RadioCard } from "@components/DesignSystem";
import Img from "@components/FileServer/Img";
import FormatDuration from "@components/Formatters/FormatDuration";
import FormattedMessage from "@components/UI/FormattedMessage";

import FlightDetail from "./FlightDetail";
import LogoBox from "./LogoBox";

export default function FlightBox(props) {
  const [showDetail, setShowDetail] = useState(false);

  const lang = useSelector(
    (state: any) => state.requestorConfig.currentLanguage
  );
  const defaultCountry = useSelector(
    (state: any) =>
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      ""
  );

  const toggleDetail = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDetail((currentShowDetail) => !currentShowDetail);
  };

  const stopFlightSelection = (e) => {
    e.stopPropagation();
  };

  const { flight, codebook } = props;
  const selectFlight = () => {
    if (typeof props.selectFlight === "function") {
      props.selectFlight(flight.Reference);
    }
  };
  const stopDetailKeySelection = (event: KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };
  const selectRadioControlFlight = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    selectFlight();
  };
  const selectCardFlight = () => {
    selectFlight();
  };
  const selectRadioControlByKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    selectFlight();
  };
  const isReturnFlight = props.direction === "return";

  const logos = flight.FlightSegments.map((flightSegment) => {
    const companyCode = flightSegment.OperatingAirline;
    let companyLogo: { name: string; url: string } | null = null;
    codebook.TransportCompanies.forEach((tcompany) => {
      if (tcompany.Code === companyCode) {
        companyLogo = { url: tcompany.LogoUrl, name: tcompany.Name };
      }
    });
    return companyLogo;
  }).filter(Boolean);

  const layover = [];

  if (flight.FlightSegments.length > 1) {
    flight.FlightSegments.forEach((flightSegment, index) => {
      if (flight.FlightSegments.length > index + 1) {
        codebook.Airports.forEach((airport) => {
          if (airport.Code === flightSegment.DestinationAirport) {
            layover.push(airport.Name);
          }
        });
      }
    });
  }

  const firstFlightSegment = flight.FlightSegments[0];
  const lastFlightSegment =
    flight.FlightSegments[flight.FlightSegments.length - 1];
  const description = {
    OriginAirport: firstFlightSegment.OriginAirport,
    layover: [...layover],
    DestinationAirport: lastFlightSegment.DestinationAirport,
  };

  const dateTimeInfo = {
    departureTime: moment(firstFlightSegment.DepartureDateTime).format(
      hourMinuteFormat(defaultCountry)
    ),
    arrivalTime: moment(lastFlightSegment.ArrivalDateTime).format(
      hourMinuteFormat(defaultCountry)
    ),
    departureDate: moment(firstFlightSegment.DepartureDateTime).format(
      dayMonthFormat(defaultCountry)
    ),
    arrivalDate: moment(lastFlightSegment.ArrivalDateTime).format(
      dayMonthFormat(defaultCountry)
    ),
    departureDay: moment(firstFlightSegment.DepartureDateTime)
      .locale(lang)
      .format("dd")
      .toUpperCase(),
  };
  const differentDay =
    dateTimeInfo.arrivalDate !== dateTimeInfo.departureDate
      ? dateTimeInfo.arrivalDate
      : null;
  const transferCount = Math.max(flight.FlightSegments.length - 1, 0);
  const flightStreamIndex = isReturnFlight ? 1 : 0;

  return (
    <RadioCard
      cardChecked={props.selected}
      className={classnames("best-offer-flight-box", {
        selected: props.selected && props.touched,
        unselected: !props.selected && props.touched,
      })}
      cardProps={{
        onClick: selectCardFlight,
      }}
      controlProps={{
        "aria-label": props.selected
          ? "Selected flight offer"
          : "Select flight offer",
        onClick: selectRadioControlFlight,
        onKeyDownCapture: selectRadioControlByKey,
        tabIndex: 0,
      }}
      value={flight.Reference}
    >
      <div className="best-offer-information-row">
        <div className="best-offer-flight-main">
          <div
            className={`flight-stream flight-stream-${flightStreamIndex} best-offer-flight-stream`}
          >
            <div className="flight-segment-header">
              <div
                className={classnames(
                  "best-offer-flight-date-pill",
                  "flight-segment-title",
                  isReturnFlight
                    ? "flight-segment-title__arrivals"
                    : "flight-segment-title__departure"
                )}
              >
                <span className="flight-segment-title-typeroute">
                  <FormattedMessage
                    id={
                      isReturnFlight
                        ? "FlightAdditionalInfo.return"
                        : "Flight.departure"
                    }
                  />
                </span>
                <span className="flight-segment-title-datum">
                  {dateTimeInfo.departureDate}
                </span>
                <span className="flight-segment-title-dayofweek">
                  {dateTimeInfo.departureDay}
                </span>
                <button
                  aria-expanded={showDetail}
                  className="flight-segment-title-link"
                  onClick={toggleDetail}
                  onKeyDown={stopDetailKeySelection}
                  onPointerDown={stopFlightSelection}
                  type="button"
                >
                  <FormattedMessage id="FlightStream.flightDetail" />
                  <Img
                    alt="down arrow icon"
                    className={classnames(
                      "flight-segment-title-link-toggle-icon",
                      {
                        "select-arrow-rotate-up": showDetail,
                      }
                    )}
                    src="/static/images/ico-arrow-down.svg"
                  />
                </button>
              </div>
            </div>
            <div className="flight-segment best-offer-flight-segment">
              <div className="flight-segment-city-time">
                <div className="flight-segment-city-from">
                  <div className="flight-segment-city">
                    {description.OriginAirport}
                  </div>
                </div>
                <div className="flight-segment-time-from">
                  <div className="flight-segment-type-icon" />
                  <div className="flight-segment-time">
                    {dateTimeInfo.departureTime}
                  </div>
                </div>
              </div>
              <div className="flight-segment-middle">
                <div className="flight-segment-line" />
                <div className="flight-segment-transfers-duration">
                  <div className="flight-segment-duration">
                    <FormatDuration duration={flight.JourneyDuration} />
                  </div>
                  <span className="flight-segment-marks-transfers-description">
                    {transferCount === 0 ? (
                      <FormattedMessage id="FlightAdditionalInfo.nonstop" />
                    ) : (
                      <>
                        {transferCount}{" "}
                        <FormattedMessage
                          id={
                            transferCount === 1
                              ? "FlightAdditionalInfo.transfer"
                              : "FlightAdditionalInfo.transfers"
                          }
                        />
                      </>
                    )}
                  </span>
                </div>
                <div className="flight-segment-line" />
                <div className="flight-segment-next-day-warning">
                  {differentDay}
                </div>
              </div>
              <div className="flight-segment-city-time">
                <div className="flight-segment-marks-to">
                  <div className="flight-segment-city">
                    {description.DestinationAirport}
                  </div>
                </div>
                <div className="flight-segment-time">
                  {dateTimeInfo.arrivalTime}
                </div>
              </div>
              <div className="flight-segment-airline">
                <LogoBox logos={logos} />
              </div>
            </div>
            {showDetail && <FlightDetail flightStream={flight} show />}
          </div>
        </div>
      </div>
    </RadioCard>
  );
}
